import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import type { ExportResponse } from "../../../../../api/types/svTypes.ts";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext.ts";
import { logError } from "@entrilia/shared/logging.ts";
import { downloadFileFromUrl } from "@entrilia/shared/services/downloadFile.ts";
import { wait } from "@entrilia/shared/utilities/promiseHelper.ts";
import biApi from "../../../../../api/biApi.ts";
import { exportFile, EXPORT_FAILED, EXPORT_TIMED_OUT } from "../components/common/export/exportFile.ts";

const dependencies = {
  checkReady: biApi.checkExportReady,
  download: async (token: string): Promise<void> => {
    await downloadFileFromUrl(biApi.generateExportDownloadLink(token));
  },
  wait,
};

export default function useExport(maxAttempts = 200): {
  exporting: boolean;
  requestExport: (request: () => Promise<ApiResponse<ExportResponse>>) => Promise<void>;
} {
  const [exporting, setExporting] = useState(false);
  const pendingRequestRef = useRef<AbortController | undefined>(undefined);
  const { sendNotificationError } = useNotificationContext();

  useEffect(() => () => pendingRequestRef.current?.abort(), []);

  const requestExport = useCallback(
    async (request: () => Promise<ApiResponse<ExportResponse>>) => {
      if (pendingRequestRef.current) {
        return;
      }
      const controller = new AbortController();
      pendingRequestRef.current = controller;
      setExporting(true);
      try {
        await exportFile(request, dependencies, controller.signal, maxAttempts);
      } catch (error) {
        if (!controller.signal.aborted) {
          logError(error, "[useExport] export file");
          sendNotificationError(
            error instanceof Error && error.message === EXPORT_TIMED_OUT ? EXPORT_TIMED_OUT : EXPORT_FAILED
          );
        }
      } finally {
        pendingRequestRef.current = undefined;
        if (!controller.signal.aborted) {
          setExporting(false);
        }
      }
    },
    [maxAttempts, sendNotificationError]
  );

  return { exporting, requestExport };
}
